<template>
	<BaseLayout
		:hide-header="true"
		:transparent-header="true"
		:footer-full="true"
		:include-padding="false"
		background-color-class="bg-color-white"
	>
		<IncomeFundHero
			:loading="loading"
			:offering-id="incomeFundOfferingId"
			:preset-plan-funded-lead-capture-config="presetPlanFundedLeadCaptureConfig"
		/>

		<section class="container marketing-section py-400-md">
			<h2 class="heading-5 mb-50-smo mb-150-md">Literature</h2>
			<BasePlaceHolder :section-count="3" :placehold="loading">
				<LiteratureSection
					v-if="showLiterature"
					:literature-list="literatureItems"
					:disclaimer="literatureDisclaimer"
					disclaimer-classes="body-lg-md mt-150"
				/>
			</BasePlaceHolder>
		</section>
		<FootnoteSection />

		<template #footer>
			<FooterFull>
				<template #additionalDisclaimer>
					<p>Fundrise Income Fund is the marketing name for Fundrise Income Real Estate Fund, LLC.</p>
					<p
						>The originating advertising material was sponsored and paid for by the Fundrise Income Real
						Estate Fund, LLC. The originating advertising material is a paid endorsement. Nothing in the
						advertising material should be construed as tax advice, an offer, recommendation, or
						solicitation to buy or sell any security. All investing includes risk, including the possible
						loss of money you invest, and past performance does not guarantee future performance. Carefully
						consider the investment objectives, risks, charges and expenses of the Fundrise Income Real
						Estate Fund, LLC before investing.</p
					>

					<br />
				</template>
			</FooterFull>
		</template>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import FooterFull from '@components/footer/footer-full.vue';
import FootnoteSection from '@components/marketing-site/private-credit/footnote-section.vue';
import { FundLiterature } from 'types/funds';
import { getFund } from '@sanity/proxy/funds';
import IncomeFundHero from '@components/marketing-site/private-credit/income-fund-hero.vue';
import { incomeFundOfferingId } from '@constants/reit';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import LiteratureSection from '@components/offering-details/literature-section.vue';
import { marketingFunnelFundIncome } from '@constants/plans-info';
import { SanityRichTextBlock } from 'types/sanity';
import { useHead } from '@unhead/vue';

const metaTitle = 'The Fundrise Income Fund';
const description =
	'The Fundrise Income Fund aims to deliver consistent cash flow, from a diversified, professionally managed portfolio of private assets.';

const loading = ref(true);

const literatureItems = ref<Array<FundLiterature>>([]);
const literatureDisclaimer = ref<Array<SanityRichTextBlock>>([]);

const presetPlanFundedLeadCaptureConfig: LeadCaptureFormConfig = {
	buttonText: 'Get started',
	presetPlanId: marketingFunnelFundIncome,
	signupFlowType: 'FUND_MARKETING',
	thirdPartyAuthTheme: 'SECONDARY'
};

const showLiterature = computed((): boolean => literatureItems.value?.length > 0);

async function setup(): Promise<void> {
	useHead({
		title: metaTitle,
		meta: [
			{ property: 'og:title', content: metaTitle },
			{ name: 'description', content: description },
			{ property: 'og:description', content: description }
		]
	});

	try {
		const fundDetails = await getFund(incomeFundOfferingId);

		literatureItems.value = fundDetails?.literature ?? [];
		literatureDisclaimer.value = fundDetails?.literatureDisclaimer ?? [];
	} catch (error) {
	} finally {
		loading.value = false;
	}
}

setup();
</script>
